
import { Component, Inject, OnInit, computed, effect, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { MenuComponent } from './shared/components/menu/menu.component';
import { SpinnerComponent } from './shared/components/spinner/spinner.component';
import { NavigationItem } from './shared/interfaces/navigation-item';
import { AuthService } from './shared/services/auth.service';
import { ModalService } from './shared/services/modal.service';
import { SharedStateService } from './shared/services/shared-state.service';
import { LoadingService } from './shared/services/loading.service';
import { AccountService } from './account/services/account.service';
import { RouterService } from './shared/services/router.service';
import * as Sentry from "@sentry/angular";
import { MatButtonModule } from '@angular/material/button';
import { filter } from 'rxjs';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    MatDialogModule,
    MatMenuModule,
    MatDivider,
    MenuComponent,
    MatButtonModule
],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {

  #routerService = inject(RouterService);
  #stateService = inject(SharedStateService);
  #router = inject(Router);
  #authService = inject(AuthService);
  #accountService = inject(AccountService);
  #modalService = inject(ModalService);
  #dialog = inject(MatDialog);
  #loadingService = inject(LoadingService);
  isStandardWebsite = signal(true);

  showMenu = computed(() => this.#stateService.IsLoggedIn() && this.#stateService.user());
  loggedInUserName = computed(() => {
    const USER = this.#stateService.user();
    return `${USER?.firstName} ${USER?.lastName}`;
  });

  menuItems = computed(() => {
    const USER = this.#stateService.user();
    let items: NavigationItem[] = [];
    if (this.#stateService.IsLoggedIn()) {
      items = USER?.navigationItems ?? [];
    }

    return items;
  });

  private loadingDialogRef: MatDialogRef<SpinnerComponent, any> | null = null;

  constructor(@Inject('APP_CONFIG') private config: any) {

    if (config?.sentryDSN.startsWith('http')) {
      Sentry.init({
        dsn: config.sentryDSN,
        // Setting this option to true will send default PII data to Sentry.
        // For example, automatic IP address collection on events
        sendDefaultPii: true,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration()
        ],
        // Tracing
        // tracesSampleRate: 1.0, //  Capture 100% of the transactions
        // // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
        // // Session Replay
        // replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        // replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
        // Enable sending logs to Sentry
        enableLogs: true,
        environment: config?.sentryEnv?.startsWith('#') ? 'Local' : config.sentryEnv
      });
    }

    /**
     * This is a signal effect (https://angular.io/guide/signals#effects)
     * it's watching the modal service show loading modal to see if we need to display a modal
     */
    effect(() => {
      if (this.loadingDialogRef) {
        this.loadingDialogRef.close();
      }
      const loading = this.#loadingService.loading();

      if (loading) {
        this.loadingDialogRef = this.#dialog.open(SpinnerComponent);
      }

      if (!loading) {
        this.loadingDialogRef?.close();
        this.loadingDialogRef = null;
      }
    });

    this.#routerService.init();
  }

  ngOnInit(): void {
    this.#router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      const url = event.urlAfterRedirects;
      const isWarrantRequest = url.includes('warrant-request');
      this.isStandardWebsite.set(!isWarrantRequest);
    });

    const isExternalValidation = window.location.href.includes('verify-payment/validate')
      || window.location.href.includes('login/tatrs-login')
      || window.location.href.includes('account/reset-password')
      || window.location.href.includes('warrant-request');

    this.#authService.init();
    this.#stateService.init()
      .subscribe({
        next: (validToken) => {
          if (!validToken && !isExternalValidation) {
            this.logout(false);
          }
        },
        error: _ => {
          if (!isExternalValidation) {
            this.logout(false);
          }
        }
      })
  }

  goHome(): void {
    if (this.#stateService.IsLoggedIn()) {
      this.#router.navigateByUrl('home');
    } else {
      this.#router.navigateByUrl('');
    }
  }

  logout(showToast: boolean = true): void {
    this.#accountService.removeUserWebsiteTracker();
    this.#authService.logout(showToast);
  }

  changePassword(): void {
    this.#router.navigateByUrl('account/change-password');
  }

  changeEmailPreferences(): void {
    this.#router.navigateByUrl('account/change-email-preferences');
  }

  changeContactInformation(): void {
    this.#router.navigateByUrl('account/change-contact-information');
  }

  navigateToWarrantRequest(): void {
    this.#router.navigate(['/warrant-request']);
  }
}
